import React, { useState, useEffect } from 'react';
import Alert from '../../common/Alert';
import commissionPaymentService from '../../../services/ShipmentCommissionPaymentService';
import { X } from 'lucide-react';

const EditPaymentModal = ({ payment, commission, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    amount:          '',
    payment_date:    '',
    payment_mode:    'CASH',
    transaction_ref: '',
    remarks:         '',
  });
  const [fileAttachment, setFileAttachment] = useState(null);
  const [filePreview, setFilePreview]       = useState(null);
  const [loading, setLoading]               = useState(false);

  const paymentModes = ['CASH', 'BANK_TRANSFER', 'UPI', 'CHEQUE', 'ONLINE'];

  useEffect(() => {
    if (payment) {
      setFormData({
        amount:          payment.amount,
        payment_date:    payment.payment_date ? payment.payment_date.split('T')[0] : '',
        payment_mode:    payment.payment_mode  || 'CASH',
        transaction_ref: payment.transaction_ref || '',
        remarks:         payment.remarks         || '',
      });
    }
  }, [payment]);

  const getMaxAmount = () => {
    const old = parseFloat(payment?.amount || 0);
    const { recipient_type, recipient_id } = payment || {};

    if (recipient_type === 'VEHICLE_OWNER') {
      const bal = parseFloat(commission?.vehicle_owner_commission_amount || 0)
                - parseFloat(commission?.vehicle_owner_paid_amount || 0);
      return (bal + old).toFixed(2);
    }
    if (recipient_type === 'COMPANY') {
      const bal = parseFloat(commission?.company_commission_amount || 0)
                - parseFloat(commission?.company_paid_amount || 0);
      return (bal + old).toFixed(2);
    }
    if (recipient_type === 'SENDER_HUB') {
      const hubs = Array.isArray(commission?.sender_hub_commissions) ? commission.sender_hub_commissions : [];
      const hub = hubs.find(h => h.hub_id === recipient_id);
      if (hub) {
        const bal = parseFloat(hub.hub_commission_amount || 0) - parseFloat(hub.paid_amount || 0);
        return (bal + old).toFixed(2);
      }
    }
    if (recipient_type === 'RECEIVER_HUB') {
      const hubs = Array.isArray(commission?.receiver_hub_commissions) ? commission.receiver_hub_commissions : [];
      const hub = hubs.find(h => h.hub_id === recipient_id);
      if (hub) {
        const bal = parseFloat(hub.hub_commission_amount || 0) - parseFloat(hub.paid_amount || 0);
        return (bal + old).toFixed(2);
      }
    }
    return old.toFixed(2);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { Alert.error('File must be under 5 MB'); return; }
    setFileAttachment(file);
    if (file.type.startsWith('image/')) {
      const r = new FileReader();
      r.onloadend = () => setFilePreview(r.result);
      r.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  const removeFile = () => {
    setFileAttachment(null);
    setFilePreview(null);
    const el = document.getElementById('edit-pm-file');
    if (el) el.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const max = parseFloat(getMaxAmount());
    if (parseFloat(formData.amount) > max) {
      Alert.error(`Amount exceeds maximum allowed: ₹${max.toLocaleString('en-IN')}`); return;
    }

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('amount',       formData.amount);
      fd.append('payment_date', formData.payment_date);
      fd.append('payment_mode', formData.payment_mode);
      if (formData.transaction_ref) fd.append('transaction_ref', formData.transaction_ref);
      if (formData.remarks)         fd.append('remarks',         formData.remarks);
      if (fileAttachment)           fd.append('file_attachment',  fileAttachment);

      await commissionPaymentService.updatePayment(payment.payment_id, fd);
      Alert.success('Payment updated successfully!');
      onSuccess();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update payment');
    } finally {
      setLoading(false);
    }
  };

  const formatRecipientType = (type) => {
    const map = {
      VEHICLE_OWNER: 'Vehicle Owner',
      COMPANY: 'Company',
      SENDER_HUB: 'Sender Hub',
      RECEIVER_HUB: 'Receiver Hub',
    };
    return map[type] || type;
  };

  const typeBadge = {
    VEHICLE_OWNER: 'bg-blue-100 text-blue-700',
    COMPANY:       'bg-green-100 text-green-700',
    SENDER_HUB:    'bg-purple-100 text-purple-700',
    RECEIVER_HUB:  'bg-indigo-100 text-indigo-700',
  }[payment?.recipient_type] || 'bg-gray-100 text-gray-700';

  const max = getMaxAmount();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-sm shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">

        <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-secondary">Edit Payment</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">

          <div className="bg-gray-50 border border-gray-200 rounded-sm p-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-xs text-gray-500">Payment ID</p>
                <p className="font-semibold">{payment?.payment_id}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Recipient</p>
                <p className="font-semibold truncate">{payment?.recipient_name}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Type</p>
                <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${typeBadge}`}>
                  {formatRecipientType(payment?.recipient_type)}
                </span>
              </div>
              <div>
                <p className="text-xs text-gray-500">Original Amount</p>
                <p className="font-bold text-blue-600">₹{parseFloat(payment?.amount || 0).toLocaleString('en-IN')}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary mb-2">
                Amount <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                placeholder="Enter amount"
                className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Max allowed: <span className="font-semibold text-blue-600">₹{parseFloat(max).toLocaleString('en-IN')}</span>
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary mb-2">
                Payment Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="payment_date"
                value={formData.payment_date}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary mb-2">
                Payment Mode <span className="text-red-500">*</span>
              </label>
              <select
                name="payment_mode"
                value={formData.payment_mode}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand"
              >
                {paymentModes.map(m => <option key={m} value={m}>{m.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary mb-2">Transaction Reference</label>
              <input
                type="text"
                name="transaction_ref"
                value={formData.transaction_ref}
                onChange={handleChange}
                placeholder="UTR / Cheque no."
                className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary mb-2">Remarks</label>
            <textarea
              name="remarks"
              value={formData.remarks}
              onChange={handleChange}
              rows="2"
              placeholder="Optional remarks..."
              className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary mb-2">Update Payment Proof (Optional)</label>
            {payment?.has_attachment && !fileAttachment && (
              <div className="mb-2">
                <button type="button"
                  onClick={() => window.open(commissionPaymentService.getFileAttachmentUrl(payment.payment_id), '_blank')}
                  className="text-brand hover:text-blue-800 text-sm">
                  📎 View Current Attachment
                </button>
              </div>
            )}
            <input
              id="edit-pm-file"
              type="file"
              accept="image/*,.pdf"
              onChange={handleFileChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand"
            />
            <p className="text-xs text-gray-500 mt-1">
              {payment?.has_attachment ? 'Upload to replace existing' : 'Images or PDF, max 5 MB'}
            </p>
            {filePreview && (
              <div className="mt-3 relative inline-block">
                <img src={filePreview} alt="Preview" className="w-28 h-28 object-cover border rounded-md" />
                <button type="button" onClick={removeFile}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center">×</button>
              </div>
            )}
            {fileAttachment && !filePreview && (
              <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                📄 {fileAttachment.name}
                <button type="button" onClick={removeFile} className="text-red-600 text-xs">Remove</button>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <button type="submit" disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-sm hover:bg-blue-700 font-medium disabled:opacity-50 transition-colors">
              {loading ? 'Updating...' : 'Update Payment'}
            </button>
            <button type="button" onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-sm hover:bg-gray-50 font-medium transition-colors">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPaymentModal;
